import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Chip } from "@/components/ui/chip";
import { canonicalStateChip } from "@/components/finding-badges";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  GithubIcon,
  JiraIcon,
  AzureDevOpsIcon,
  LinearIcon,
} from "@/components/icons";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Layers,
  Save,
  X,
} from "lucide-react";

interface TrackerTargetSectionProps {
  projectId: string;
}

type Provider = "github" | "linear" | "jira" | "azure_devops";

const DEFAULT_GITHUB_WONT_DO = ["not_planned", "duplicate", "wontfix"];

const DEFAULT_JIRA_WONT_DO = [
  "Won't Do",
  "Duplicate",
  "Cannot Reproduce",
  "By Design",
];

const DEFAULT_ADO_WONT_DO = [
  "Won't Fix",
  "As Designed",
  "Cannot Reproduce",
  "Duplicate",
  "Deferred",
];

const DEFAULT_LINEAR_WONT_DO = [
  "Canceled",
  "Duplicate",
  "Won't Fix",
  "Invalid",
];

const GITHUB_QUICK_REASONS = ["not_planned", "duplicate", "wontfix", "invalid"];

export function TrackerTargetSection({ projectId }: TrackerTargetSectionProps) {
  const [selectedProvider, setSelectedProvider] = useState<Provider>("github");

  const { data: allConnections, isLoading: connectionsLoading } =
    trpc.integration.listConnections.useQuery();

  const { data: projectData } = trpc.project.getById.useQuery({
    id: projectId,
  });

  const {
    data: existingTarget,
    isLoading: targetLoading,
    refetch: refetchTarget,
  } = trpc.workflow.integrationTarget.get.useQuery({
    projectId,
  });

  const githubConnections =
    allConnections?.filter((c) => c.provider === "github") ?? [];
  const linearConnections =
    allConnections?.filter((c) => c.provider === "linear") ?? [];
  const jiraConnections =
    allConnections?.filter((c) => c.provider === "jira") ?? [];
  const adoConnections =
    allConnections?.filter((c) => c.provider === "azure_devops") ?? [];

  const [selectedConnectionId, setSelectedConnectionId] = useState<string>("");
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>("");
  const [defaultIssueType, setDefaultIssueType] = useState<string>("Issue");
  const [wontDoResolutions, setWontDoResolutions] = useState<string[]>(
    DEFAULT_GITHUB_WONT_DO,
  );
  const [customResolutionInput, setCustomResolutionInput] =
    useState<string>("");

  // Sync state when existing target loads
  useEffect(() => {
    if (existingTarget) {
      if (
        existingTarget.provider === "github" ||
        existingTarget.provider === "linear" ||
        existingTarget.provider === "jira" ||
        existingTarget.provider === "azure_devops"
      ) {
        setSelectedProvider(existingTarget.provider);
      }
      setSelectedConnectionId(existingTarget.connectionId);
      setSelectedProjectKey(existingTarget.targetKey);
      setDefaultIssueType(
        existingTarget.defaultIssueType ||
          (existingTarget.provider === "github" ? "Issue" : "Bug"),
      );
      if (Array.isArray(existingTarget.wontDoResolutions)) {
        const seen = new Set<string>();
        const deduped: string[] = [];
        for (const item of existingTarget.wontDoResolutions as string[]) {
          const norm = item.trim().toLowerCase().replace(/\s+/g, "_");
          if (norm && !seen.has(norm)) {
            seen.add(norm);
            deduped.push(item);
          }
        }
        setWontDoResolutions(deduped);
      }
    } else {
      // Default priority: GitHub -> Linear -> Jira -> Azure DevOps if connection exists
      if (githubConnections.length > 0) {
        setSelectedProvider("github");
        setSelectedConnectionId(githubConnections[0].id);
        setDefaultIssueType("Issue");
        setWontDoResolutions(DEFAULT_GITHUB_WONT_DO);
      } else if (linearConnections.length > 0) {
        setSelectedProvider("linear");
        setSelectedConnectionId(linearConnections[0].id);
        setDefaultIssueType("Bug");
        setWontDoResolutions(DEFAULT_LINEAR_WONT_DO);
      } else if (jiraConnections.length > 0) {
        setSelectedProvider("jira");
        setSelectedConnectionId(jiraConnections[0].id);
        setDefaultIssueType("Bug");
        setWontDoResolutions(DEFAULT_JIRA_WONT_DO);
      } else if (adoConnections.length > 0) {
        setSelectedProvider("azure_devops");
        setSelectedConnectionId(adoConnections[0].id);
        setDefaultIssueType("Bug");
        setWontDoResolutions(DEFAULT_ADO_WONT_DO);
      }
    }
  }, [
    existingTarget,
    githubConnections.length,
    linearConnections.length,
    jiraConnections.length,
    adoConnections.length,
  ]);

  const isGithub = selectedProvider === "github";
  const isLinear = selectedProvider === "linear";
  const isJira = selectedProvider === "jira";
  const isAdo = selectedProvider === "azure_devops";

  const activeConnections = isGithub
    ? githubConnections
    : isLinear
      ? linearConnections
      : isJira
        ? jiraConnections
        : adoConnections;

  const activeConnection = activeConnections.find(
    (c) => c.id === selectedConnectionId,
  );
  const isConnectionActive = activeConnection?.status === "active";

  // When provider toggles, set first connection of that provider if not set
  const handleProviderToggle = (provider: Provider) => {
    setSelectedProvider(provider);
    setSelectedProjectKey("");
    const conns =
      provider === "github"
        ? githubConnections
        : provider === "linear"
          ? linearConnections
          : provider === "jira"
            ? jiraConnections
            : adoConnections;
    if (conns.length > 0) {
      setSelectedConnectionId(conns[0].id);
    } else {
      setSelectedConnectionId("");
    }
    setDefaultIssueType(provider === "github" ? "Issue" : "Bug");
    setWontDoResolutions(
      provider === "github"
        ? DEFAULT_GITHUB_WONT_DO
        : provider === "linear"
          ? DEFAULT_LINEAR_WONT_DO
          : provider === "jira"
            ? DEFAULT_JIRA_WONT_DO
            : DEFAULT_ADO_WONT_DO,
    );
  };

  // ── GitHub Queries ──────────────────────────────────────────
  const {
    data: githubRepos,
    isLoading: githubReposLoading,
    error: githubReposError,
  } = trpc.github.listRepositories.useQuery(
    {},
    { enabled: isGithub && !!selectedConnectionId && isConnectionActive },
  );

  const { data: githubConnectUrl } = trpc.github.connectUrl.useQuery(
    undefined,
    {
      enabled: isGithub && githubConnections.length === 0,
    },
  );

  // Auto-select repository if matched with project linked code repository
  useEffect(() => {
    if (
      isGithub &&
      githubRepos &&
      githubRepos.length > 0 &&
      !selectedProjectKey
    ) {
      if (projectData?.githubRepositoryId) {
        const linked = githubRepos.find(
          (r) => r.id === projectData.githubRepositoryId,
        );
        if (linked) {
          setSelectedProjectKey(linked.fullName);
          return;
        }
      }
      setSelectedProjectKey(githubRepos[0].fullName);
    }
  }, [
    isGithub,
    githubRepos,
    selectedProjectKey,
    projectData?.githubRepositoryId,
  ]);

  // ── Jira Queries ──────────────────────────────────────────
  const {
    data: jiraProjects,
    isLoading: jiraProjectsLoading,
    error: jiraProjectsError,
  } = trpc.integration.jira.listProjects.useQuery(
    { connectionId: selectedConnectionId },
    { enabled: isJira && !!selectedConnectionId && isConnectionActive },
  );

  const { data: jiraIssueTypes, isLoading: jiraIssueTypesLoading } =
    trpc.integration.jira.listIssueTypes.useQuery(
      {
        connectionId: selectedConnectionId,
        projectIdOrKey: selectedProjectKey || undefined,
      },
      {
        enabled:
          isJira &&
          !!selectedConnectionId &&
          !!selectedProjectKey &&
          isConnectionActive,
      },
    );

  // Auto-align defaultIssueType with available issue types (handles localization like Task -> Uppgift)
  useEffect(() => {
    if (isJira && jiraIssueTypes && jiraIssueTypes.length > 0) {
      const nonSubtasks = jiraIssueTypes.filter((t) => !t.subtask);
      const exact = nonSubtasks.find((t) => t.name === defaultIssueType);
      if (!exact) {
        const untranslated = nonSubtasks.find(
          (t) =>
            t.untranslatedName?.toLowerCase() ===
            defaultIssueType?.toLowerCase(),
        );
        if (untranslated) {
          setDefaultIssueType(untranslated.name);
        } else if (nonSubtasks.length > 0 && !defaultIssueType) {
          setDefaultIssueType(nonSubtasks[0].name);
        }
      }
    }
  }, [isJira, jiraIssueTypes, defaultIssueType]);

  const { data: jiraResolutions } =
    trpc.integration.jira.listResolutions.useQuery(
      { connectionId: selectedConnectionId },
      { enabled: isJira && !!selectedConnectionId && isConnectionActive },
    );

  // ── Azure DevOps Queries ──────────────────────────────────
  const {
    data: adoProjects,
    isLoading: adoProjectsLoading,
    error: adoProjectsError,
  } = trpc.integration.azureDevOps.listProjects.useQuery(
    { connectionId: selectedConnectionId },
    { enabled: isAdo && !!selectedConnectionId && isConnectionActive },
  );

  const { data: adoWorkItemTypes, isLoading: adoWorkItemTypesLoading } =
    trpc.integration.azureDevOps.listWorkItemTypes.useQuery(
      {
        connectionId: selectedConnectionId,
        project: selectedProjectKey,
      },
      {
        enabled:
          isAdo &&
          !!selectedConnectionId &&
          !!selectedProjectKey &&
          isConnectionActive,
      },
    );

  const { data: adoStates } = trpc.integration.azureDevOps.listStates.useQuery(
    {
      connectionId: selectedConnectionId,
      project: selectedProjectKey,
      workItemType: defaultIssueType,
    },
    {
      enabled:
        isAdo &&
        !!selectedConnectionId &&
        !!selectedProjectKey &&
        !!defaultIssueType &&
        isConnectionActive,
    },
  );

  // ── Linear Queries ─────────────────────────────────────────
  const {
    data: linearTeams,
    isLoading: linearTeamsLoading,
    error: linearTeamsError,
  } = trpc.integration.linear.listTeams.useQuery(
    { connectionId: selectedConnectionId },
    { enabled: isLinear && !!selectedConnectionId && isConnectionActive },
  );

  const { data: linearStates, isLoading: linearStatesLoading } =
    trpc.integration.linear.listWorkflowStates.useQuery(
      {
        connectionId: selectedConnectionId,
        teamId: selectedProjectKey || undefined,
      },
      {
        enabled:
          isLinear &&
          !!selectedConnectionId &&
          !!selectedProjectKey &&
          isConnectionActive,
      },
    );

  const saveMutation = trpc.workflow.integrationTarget.save.useMutation({
    onSuccess: () => {
      const providerName = isGithub
        ? "GitHub Issues"
        : isLinear
          ? "Linear"
          : isJira
            ? "Jira"
            : "Azure DevOps";
      toast.success(`${providerName} tracker target saved successfully`);
      refetchTarget();
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to save tracker target");
    },
  });

  const handleSave = () => {
    const providerName = isGithub
      ? "GitHub Issues"
      : isLinear
        ? "Linear"
        : isJira
          ? "Jira"
          : "Azure DevOps";

    if (!selectedConnectionId) {
      toast.error(`Please select a ${providerName} connection`);
      return;
    }
    if (!selectedProjectKey) {
      toast.error(
        `Please select a target ${
          isGithub
            ? "GitHub repository"
            : isLinear
              ? "Linear team"
              : isJira
                ? "Jira project"
                : "Azure DevOps project"
        }`,
      );
      return;
    }

    let targetName = selectedProjectKey;
    if (isGithub) {
      targetName = selectedProjectKey;
    } else if (isJira) {
      const selectedProj = jiraProjects?.find(
        (p) => p.key === selectedProjectKey,
      );
      if (selectedProj?.name) targetName = selectedProj.name;
    } else if (isAdo) {
      const selectedProj = adoProjects?.find(
        (p) => p.name === selectedProjectKey || p.id === selectedProjectKey,
      );
      if (selectedProj?.name) targetName = selectedProj.name;
    } else if (isLinear) {
      const selectedTeam = linearTeams?.find(
        (t) => t.id === selectedProjectKey,
      );
      if (selectedTeam?.name) targetName = selectedTeam.name;
    }

    const statusCategoryMap: Record<string, string> = isGithub
      ? {
          open: "open",
          "closed:completed": "fix_applied",
          "closed:not_planned": "wont_do",
        }
      : isJira
        ? {
            "To Do": "open",
            "In Progress": "in_progress",
            Done: "fix_applied",
          }
        : isAdo
          ? (adoStates ?? []).reduce(
              (acc, state) => {
                const cat = state.category.toLowerCase();
                if (cat === "proposed") acc[state.name] = "open";
                else if (cat === "inprogress" || cat === "in_progress")
                  acc[state.name] = "in_progress";
                else if (cat === "resolved") acc[state.name] = "fix_applied";
                else if (cat === "completed") acc[state.name] = "fix_applied";
                else if (cat === "removed") acc[state.name] = "deleted";
                return acc;
              },
              {} as Record<string, string>,
            )
          : (linearStates ?? []).reduce(
              (acc, state) => {
                const t = (state.type || "").toLowerCase();
                if (t === "completed") acc[state.name] = "fix_applied";
                else if (t === "canceled") acc[state.name] = "wont_do";
                else if (t === "started") acc[state.name] = "in_progress";
                else acc[state.name] = "open";
                return acc;
              },
              {} as Record<string, string>,
            );

    saveMutation.mutate({
      projectId,
      connectionId: selectedConnectionId,
      provider: selectedProvider,
      targetKey: selectedProjectKey,
      targetName,
      defaultIssueType: defaultIssueType || (isGithub ? "Issue" : "Bug"),
      wontDoResolutions,
      statusCategoryMap,
      isDefault: true,
    });
  };

  const handleAddCustomResolution = () => {
    const trimmed = customResolutionInput.trim();
    if (!trimmed) return;
    if (!wontDoResolutions.includes(trimmed)) {
      setWontDoResolutions([...wontDoResolutions, trimmed]);
    }
    setCustomResolutionInput("");
  };

  const handleToggleResolution = (resName: string) => {
    if (wontDoResolutions.includes(resName)) {
      setWontDoResolutions(wontDoResolutions.filter((r) => r !== resName));
    } else {
      setWontDoResolutions([...wontDoResolutions, resName]);
    }
  };

  if (connectionsLoading || targetLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  const hasAnyConnections =
    githubConnections.length > 0 ||
    linearConnections.length > 0 ||
    jiraConnections.length > 0 ||
    adoConnections.length > 0;

  if (!hasAnyConnections) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Issue Tracker Target</CardTitle>
          </div>
          <CardDescription>
            Map this project to an external issue tracker (GitHub Issues,
            Linear, Jira Cloud, or Azure DevOps) to automatically export
            findings and synchronize status.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="font-medium text-foreground">
                No issue tracker connections found
              </p>
              <p className="text-xs text-muted-foreground">
                Connect your GitHub App, Linear workspace, Jira Cloud site, or
                Azure DevOps organization in Settings → Integrations to enable
                issue tracking for this project.
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/settings/integrations">
                Go to Integrations
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Issue Tracker Target</CardTitle>
          </div>
          <CardDescription>
            Select a primary issue tracker to automatically create tickets from
            findings and synchronize resolution status.
          </CardDescription>
        </div>

        {/* Provider Switcher */}
        <div className="space-y-2 pt-2">
          <Label className="text-xs font-medium text-muted-foreground">
            Tracker provider
          </Label>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "gap-2 h-8 text-xs",
                isGithub &&
                  "border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background font-semibold shadow-xs",
              )}
              onClick={() => handleProviderToggle("github")}
            >
              <GithubIcon className="h-3.5 w-3.5" />
              GitHub Issues
              {existingTarget?.provider === "github" && (
                <Badge
                  variant={isGithub ? "secondary" : "outline"}
                  className="text-xs px-1.5 py-0 font-normal"
                >
                  Active
                </Badge>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "gap-2 h-8 text-xs",
                isLinear &&
                  "border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background font-semibold shadow-xs",
              )}
              onClick={() => handleProviderToggle("linear")}
            >
              <LinearIcon className="h-3.5 w-3.5" />
              Linear
              {existingTarget?.provider === "linear" && (
                <Badge
                  variant={isLinear ? "secondary" : "outline"}
                  className="text-xs px-1.5 py-0 font-normal"
                >
                  Active
                </Badge>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "gap-2 h-8 text-xs",
                isJira &&
                  "border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background font-semibold shadow-xs",
              )}
              onClick={() => handleProviderToggle("jira")}
            >
              <JiraIcon className="h-3.5 w-3.5" />
              Jira Cloud
              {existingTarget?.provider === "jira" && (
                <Badge
                  variant={isJira ? "secondary" : "outline"}
                  className="text-xs px-1.5 py-0 font-normal"
                >
                  Active
                </Badge>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "gap-2 h-8 text-xs",
                isAdo &&
                  "border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background font-semibold shadow-xs",
              )}
              onClick={() => handleProviderToggle("azure_devops")}
            >
              <AzureDevOpsIcon className="h-3.5 w-3.5" />
              Azure DevOps
              {existingTarget?.provider === "azure_devops" && (
                <Badge
                  variant={isAdo ? "secondary" : "outline"}
                  className="text-xs px-1.5 py-0 font-normal"
                >
                  Active
                </Badge>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Empty state for selected provider */}
        {activeConnections.length === 0 ? (
          <div className="rounded-lg border border-dashed p-4 text-xs text-muted-foreground flex items-center justify-between">
            <span>
              No{" "}
              {isGithub
                ? "GitHub App"
                : isLinear
                  ? "Linear"
                  : isJira
                    ? "Jira"
                    : "Azure DevOps"}{" "}
              connections found.
            </span>
            <Button asChild size="sm" variant="outline">
              {isGithub && githubConnectUrl?.url ? (
                <a
                  href={githubConnectUrl.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gap-1.5 inline-flex items-center"
                >
                  Install GitHub App
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <Link to="/settings/integrations">
                  Connect{" "}
                  {isGithub
                    ? "GitHub"
                    : isLinear
                      ? "Linear"
                      : isJira
                        ? "Jira"
                        : "Azure DevOps"}
                </Link>
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Connection status warning */}
            {activeConnection?.status === "needs_reauth" && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-600 dark:text-amber-400">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    Selected{" "}
                    {isGithub
                      ? "GitHub"
                      : isLinear
                        ? "Linear"
                        : isJira
                          ? "Jira"
                          : "Azure DevOps"}{" "}
                    connection requires re-authorization.
                  </span>
                </div>
                <Button asChild size="sm" variant="destructive">
                  <Link to="/settings/integrations">Reconnect</Link>
                </Button>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Connection Selector */}
              <div className="space-y-2 min-w-0">
                <Label htmlFor="tracker-connection">
                  Connected{" "}
                  {isGithub
                    ? "GitHub Organization / Account"
                    : isLinear
                      ? "Linear Workspace"
                      : isJira
                        ? "Jira Site"
                        : "Organization"}
                </Label>
                <Select
                  value={selectedConnectionId}
                  onValueChange={setSelectedConnectionId}
                >
                  <SelectTrigger
                    id="tracker-connection"
                    className="w-full min-w-0"
                  >
                    <SelectValue
                      placeholder={`Select ${
                        isGithub
                          ? "GitHub account"
                          : isLinear
                            ? "Linear workspace"
                            : isJira
                              ? "Jira site"
                              : "organization"
                      }...`}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeConnections.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.displayName || c.siteUrl || c.externalSiteId} (
                        {c.status === "active" ? "Active" : c.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target Project / Repo Selector */}
              <div className="space-y-2 min-w-0">
                <Label htmlFor="tracker-project">
                  Target{" "}
                  {isGithub
                    ? "GitHub Repository"
                    : isLinear
                      ? "Linear Team"
                      : isJira
                        ? "Jira Project"
                        : "Azure DevOps Project"}
                </Label>
                {isGithub ? (
                  githubReposLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : githubReposError ? (
                    <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2.5 text-xs text-destructive break-words">
                      Failed to load repositories: {githubReposError.message}
                    </div>
                  ) : (
                    <Select
                      value={selectedProjectKey}
                      onValueChange={setSelectedProjectKey}
                      disabled={!isConnectionActive}
                    >
                      <SelectTrigger
                        id="tracker-project"
                        className="w-full min-w-0"
                      >
                        <SelectValue placeholder="Select GitHub repository..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          new Map(
                            (githubRepos ?? []).map((repo) => [
                              repo.fullName,
                              repo,
                            ]),
                          ).values(),
                        ).map((repo) => (
                          <SelectItem key={repo.id} value={repo.fullName}>
                            <span className="font-semibold">
                              {repo.fullName}
                            </span>
                            {projectData?.githubRepositoryId === repo.id && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (Linked code repo)
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : isLinear ? (
                  linearTeamsLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : linearTeamsError ? (
                    <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2.5 text-xs text-destructive break-words">
                      Failed to load teams: {linearTeamsError.message}
                    </div>
                  ) : (
                    <Select
                      value={selectedProjectKey}
                      onValueChange={setSelectedProjectKey}
                      disabled={!isConnectionActive}
                    >
                      <SelectTrigger
                        id="tracker-project"
                        className="w-full min-w-0"
                      >
                        <SelectValue placeholder="Select Linear team..." />
                      </SelectTrigger>
                      <SelectContent>
                        {linearTeams?.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="font-semibold">{t.key}</span> —{" "}
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : isJira ? (
                  jiraProjectsLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : jiraProjectsError ? (
                    <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2.5 text-xs text-destructive break-words">
                      Failed to load projects: {jiraProjectsError.message}
                    </div>
                  ) : (
                    <Select
                      value={selectedProjectKey}
                      onValueChange={setSelectedProjectKey}
                      disabled={!isConnectionActive}
                    >
                      <SelectTrigger
                        id="tracker-project"
                        className="w-full min-w-0"
                      >
                        <SelectValue placeholder="Select Jira project..." />
                      </SelectTrigger>
                      <SelectContent>
                        {jiraProjects?.map((p) => (
                          <SelectItem key={p.id} value={p.key}>
                            <span className="font-semibold">{p.key}</span> —{" "}
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : adoProjectsLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : adoProjectsError ? (
                  <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2.5 text-xs text-destructive break-words">
                    Failed to load projects: {adoProjectsError.message}
                  </div>
                ) : (
                  <Select
                    value={selectedProjectKey}
                    onValueChange={setSelectedProjectKey}
                    disabled={!isConnectionActive}
                  >
                    <SelectTrigger
                      id="tracker-project"
                      className="w-full min-w-0"
                    >
                      <SelectValue placeholder="Select Azure DevOps project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {adoProjects?.map((p) => (
                        <SelectItem key={p.id} value={p.name}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Default Work Item / Issue Type */}
              <div className="space-y-2 min-w-0">
                <Label htmlFor="tracker-issue-type">
                  Default {isAdo ? "Work Item" : "Issue"} Type
                </Label>
                {isGithub ? (
                  <Select
                    value={defaultIssueType}
                    onValueChange={setDefaultIssueType}
                    disabled={!isConnectionActive}
                  >
                    <SelectTrigger
                      id="tracker-issue-type"
                      className="w-full min-w-0"
                    >
                      <SelectValue placeholder="Select issue type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Issue">Issue</SelectItem>
                      <SelectItem value="Bug">Bug</SelectItem>
                      <SelectItem value="Task">Task</SelectItem>
                      <SelectItem value="Improvement">Improvement</SelectItem>
                    </SelectContent>
                  </Select>
                ) : isLinear ? (
                  <Select
                    value={defaultIssueType}
                    onValueChange={setDefaultIssueType}
                    disabled={!isConnectionActive}
                  >
                    <SelectTrigger
                      id="tracker-issue-type"
                      className="w-full min-w-0"
                    >
                      <SelectValue placeholder="Select issue type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bug">Bug</SelectItem>
                      <SelectItem value="Issue">Issue</SelectItem>
                      <SelectItem value="Improvement">Improvement</SelectItem>
                    </SelectContent>
                  </Select>
                ) : isJira ? (
                  jiraIssueTypesLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select
                      value={defaultIssueType}
                      onValueChange={setDefaultIssueType}
                      disabled={!isConnectionActive}
                    >
                      <SelectTrigger
                        id="tracker-issue-type"
                        className="w-full min-w-0"
                      >
                        <SelectValue placeholder="Select issue type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {jiraIssueTypes && jiraIssueTypes.length > 0 ? (
                          jiraIssueTypes
                            .filter((t) => !t.subtask)
                            .map((t) => (
                              <SelectItem key={t.id} value={t.name}>
                                {t.name}
                              </SelectItem>
                            ))
                        ) : (
                          <>
                            <SelectItem value="Bug">Bug</SelectItem>
                            <SelectItem value="Task">Task</SelectItem>
                            <SelectItem value="Story">Story</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  )
                ) : adoWorkItemTypesLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={defaultIssueType}
                    onValueChange={setDefaultIssueType}
                    disabled={!isConnectionActive}
                  >
                    <SelectTrigger
                      id="tracker-issue-type"
                      className="w-full min-w-0"
                    >
                      <SelectValue placeholder="Select work item type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {adoWorkItemTypes && adoWorkItemTypes.length > 0 ? (
                        adoWorkItemTypes.map((t) => (
                          <SelectItem key={t.name} value={t.name}>
                            {t.name}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Bug">Bug</SelectItem>
                          <SelectItem value="Issue">Issue</SelectItem>
                          <SelectItem value="Task">Task</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Won't-Do Resolutions / Reasons */}
            <div className="space-y-3">
              <div>
                <Label>Won't-Do Reasons / Resolutions</Label>
                <p className="text-xs text-muted-foreground">
                  When a work item moves to Closed or Completed with one of
                  these reasons, the canonical finding transitions to{" "}
                  <strong>Risk accepted</strong> instead of{" "}
                  <strong>Fix applied</strong>.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {wontDoResolutions.map((res) => (
                  <Badge
                    key={res}
                    variant="secondary"
                    className="gap-1.5 py-1 px-2.5 text-xs font-medium"
                  >
                    {res}
                    <button
                      type="button"
                      onClick={() => handleToggleResolution(res)}
                      className="rounded-full hover:bg-muted p-0.5"
                      aria-label={`Remove ${res}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {isGithub && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs text-muted-foreground">
                    Available GitHub reasons (click to toggle):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {GITHUB_QUICK_REASONS.map((r) => {
                      const isSelected = wontDoResolutions.includes(r);
                      return (
                        <Button
                          key={r}
                          type="button"
                          size="sm"
                          variant={isSelected ? "secondary" : "outline"}
                          className={`h-7 text-xs ${isSelected ? "border-primary/40" : ""}`}
                          onClick={() => handleToggleResolution(r)}
                        >
                          {isSelected && (
                            <CheckCircle2 className="mr-1 h-3 w-3 text-primary" />
                          )}
                          {r}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isLinear && linearStates && linearStates.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs text-muted-foreground">
                    Available from Linear states (click to toggle):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {linearStates.map((s) => {
                      const isSelected = wontDoResolutions.includes(s.name);
                      return (
                        <Button
                          key={s.id}
                          type="button"
                          size="sm"
                          variant={isSelected ? "secondary" : "outline"}
                          className={`h-7 text-xs ${isSelected ? "border-primary/40" : ""}`}
                          onClick={() => handleToggleResolution(s.name)}
                        >
                          {isSelected && (
                            <CheckCircle2 className="mr-1 h-3 w-3 text-primary" />
                          )}
                          {s.name} ({s.type})
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isJira && jiraResolutions && jiraResolutions.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs text-muted-foreground">
                    Available from Jira (click to toggle):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {jiraResolutions.map((r) => {
                      const isSelected = wontDoResolutions.includes(r.name);
                      return (
                        <Button
                          key={r.id}
                          type="button"
                          size="sm"
                          variant={isSelected ? "secondary" : "outline"}
                          className={`h-7 text-xs ${isSelected ? "border-primary/40" : ""}`}
                          onClick={() => handleToggleResolution(r.name)}
                        >
                          {isSelected && (
                            <CheckCircle2 className="mr-1 h-3 w-3 text-primary" />
                          )}
                          {r.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 max-w-md pt-1">
                <Input
                  type="text"
                  value={customResolutionInput}
                  onChange={(e) => setCustomResolutionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomResolution();
                    }
                  }}
                  placeholder="Add custom reason or resolution name..."
                  className="h-9 text-xs"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-9 text-xs shrink-0"
                  onClick={handleAddCustomResolution}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Status Mapping Overview */}
            <div className="space-y-3 pt-3 border-t">
              <div>
                <Label>State Synchronization Map</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  How external{" "}
                  {isGithub
                    ? "GitHub"
                    : isLinear
                      ? "Linear"
                      : isJira
                        ? "Jira"
                        : "Azure DevOps"}{" "}
                  issue states sync back to canonical finding lifecycle states.
                </p>
              </div>

              <div className="divide-y divide-border/50 border-y border-border/60 text-xs">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground">
                  <span>
                    {isGithub
                      ? "GitHub Issue State"
                      : isLinear
                        ? "Linear State"
                        : isJira
                          ? "Jira Issue Status"
                          : "Work Item State"}
                  </span>
                  <span className="sr-only">Maps to</span>
                  <span className="justify-self-end">
                    Canonical Finding State
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-4 py-2.5">
                  <span className="font-medium text-foreground">
                    {isGithub
                      ? "Open"
                      : isLinear
                        ? "Backlog / Triage"
                        : isJira
                          ? "To Do / Backlog"
                          : "New / Proposed"}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground justify-self-center shrink-0" />
                  <div className="justify-self-end">
                    <Chip tone="outline">Open</Chip>
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-4 py-2.5">
                  <span className="font-medium text-foreground">
                    {isGithub
                      ? "Open (Assigned / In progress)"
                      : isLinear
                        ? "In Progress / Started"
                        : isJira
                          ? "In Progress"
                          : "Active / In Progress"}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground justify-self-center shrink-0" />
                  <div className="justify-self-end">
                    <Chip
                      tone={
                        canonicalStateChip("in_progress")?.tone ?? "primary"
                      }
                    >
                      {canonicalStateChip("in_progress")?.label ??
                        "In progress"}
                    </Chip>
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-4 py-2.5">
                  <span className="font-medium text-foreground">
                    {isGithub
                      ? "Closed (completed)"
                      : isLinear
                        ? "Done / Completed"
                        : isJira
                          ? "Done (Standard resolution)"
                          : "Resolved / Closed (Standard)"}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground justify-self-center shrink-0" />
                  <div className="justify-self-end">
                    <Chip
                      tone={
                        canonicalStateChip("fix_applied")?.tone ?? "primary"
                      }
                    >
                      {canonicalStateChip("fix_applied")?.label ??
                        "Fix applied"}
                    </Chip>
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-4 py-2.5">
                  <span className="font-medium text-foreground">
                    {isGithub
                      ? "Closed (not_planned / won't-do reason)"
                      : isLinear
                        ? "Canceled (Won't-do reason)"
                        : isJira
                          ? "Done (Won't-do resolution)"
                          : "Closed (Won't-fix reason)"}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground justify-self-center shrink-0" />
                  <div className="justify-self-end">
                    <Chip
                      tone={
                        canonicalStateChip("risk_accepted")?.tone ?? "muted"
                      }
                    >
                      {canonicalStateChip("risk_accepted")?.label ??
                        "Risk accepted"}
                    </Chip>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="justify-end border-t border-border/50 pt-4">
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending || !isConnectionActive}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? "Saving..." : "Save Settings"}
        </Button>
      </CardFooter>
    </Card>
  );
}
